/**
 * @module app.core
 */

import { autoinject, bindable, bindingMode } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting, StandardLoadOptions } from 'app/shared/lib/essette-site';
import { INotesConfig } from 'app/core/models/essette-interfaces';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { INoteFlagSearchFilters, NoteService } from 'app/core/services/note-service';
import { apiCall } from 'app/shared/lib/essette-api';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { Toaster } from 'app/shared/lib/toaster';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { I18N } from 'aurelia-i18n';
import { localSort } from 'app/shared/lib/sorting';
import { deepClone } from 'app/shared/lib/utilities';
import { IPopup } from 'app/shared/elements/popup-base';
import { validate } from 'app/shared/elements/ess-rule';
import { EventAggregator } from 'aurelia-event-aggregator';
import { NoteCategoryReloadEvent } from 'app/core/lib/events';
import * as Core from 'app/core/models/essette-api';

interface CustomNoteCategory extends Core.NoteCategory {
    flagsDisplay?: string | null;
    flagsList?: string[] | null;
}

@autoinject
export class NoteCategoriesGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.oneTime })
    protected notesConfig?: INotesConfig;

    protected noteCategories: CustomNoteCategory[] = [];
    protected editedNoteCategory: Core.NoteCategory = {};
    protected noteFlags: Core.NoteFlag[] = [];

    private apiSorting = new Sorting();
    private localSorting = new Sorting();
    private localSortProperties: string[] = ['flagsDisplay'];
    protected pageSize = 25;
    protected resultCount = 0;
    protected includeInactive = false;

    protected validate = validate;
    protected isValid = false;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    protected captionPrefix: string | null = null;

    ////****************** Element References ***************************************////
    private $pager!: EssPagerCustomElement;
    private $modal!: IPopup;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: NoteService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        // verify that component is properly configured
        if(!this.notesConfig) throw new Error('Bindable property [notes-config] was not set');
        if(!this.notesConfig?.serviceRootUrl) throw new Error('[serviceRootUrl] was not configured on bindable property [notes-config]');
        this.service.configure(this.notesConfig);
    }

    protected async attached(): Promise<void> {
        // Note [JP] Need to load in separate awaits to ensure note flags are loaded first
        await this.loadNoteFlags();
        await this.reload();
    }

    ////****************** Events ***************************************************////
    protected async handleFormSuccess(): Promise<void> {
        this.$modal.close();
        // trigger a reload on the Note Sub Categories tab
        this.events.publish(new NoteCategoryReloadEvent());
        await this.reload();
    }

    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;

        //If LocalSort
        if(this.localSortProperties.indexOf(property) > -1) {
            const { localSorting } = this;
            const noteCategories = this.noteCategories;
            if(direction === null) {
                this.localSorting.remove(property);
            } else {
                this.localSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            localSort(noteCategories, localSorting);
            if(this.localSorting.entries.length == 0) {
                this.reload();
            };
        }
        // Else If API Sort
        else {
            const { apiSorting } = this;
            if(direction === null) {
                apiSorting.remove(property);
            } else {
                apiSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            await this.reload();
        }
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadNoteCategories();
    }

    protected saveNoteCategory(noteCategory: Core.NoteCategory = {}): void {
        this.editedNoteCategory = deepClone(noteCategory);
        this.$modal.open();
    }

    // Set Custom Note Category Details
    private setNoteCategoryDetails(noteCategories: CustomNoteCategory[]): void {
        noteCategories?.forEach(noteCategory => {
            // Configure Flags column data
            if(!noteCategory?.flags || !noteCategory.flags.length) {
                // if no flags, display nothing at all
                noteCategory.flagsDisplay = '';
            } else if(noteCategory.flags.length === 1) {
                // if only one flag, display that flag
                noteCategory.flagsDisplay = noteCategory.flags[0].description;
            } else if(noteCategory.flags.length >= this.noteFlags.length) {
                // if all flags (or somehow more), display "All Flags"
                noteCategory.flagsDisplay = this.i18n.tr('ui:labels.all', { what: this.i18n.tr('core:note.flag.$', { count: 2 }) });
            } else {
                // otherwise, display "n Flags" and generate a list for the tooltip
                noteCategory.flagsDisplay = `${noteCategory.flags.length} ` + this.i18n.tr('core:note.flag.$', { count: 2 });
                noteCategory.flagsList = noteCategory.flags.flatMap(f => f.description ?? '')
                    .sort((a, b) => a.localeCompare(b));
            }
        });
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadNoteFlags(): Promise<void> {
        const options: StandardLoadOptions = { all: true };
        const searchFilters: INoteFlagSearchFilters = { allowCategories: true };

        try {
            this.state.beginLoad();
            const response = await this.service.loadNoteFlags(options, [], searchFilters);
            this.noteFlags = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadNoteCategories(): Promise<void> {
        const options: StandardLoadOptions = {
            sorting: this.apiSorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: this.includeInactive
        };

        const includes = ['flags'];

        try {
            this.state.beginLoad();
            const response = await this.service.loadCategories(options, includes, false);
            this.setNoteCategoryDetails(<CustomNoteCategory[]>response.results);
            this.noteCategories = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateNoteCategory(noteCategory: Core.NoteCategory): Promise<void> {
        if(!noteCategory.id) return;

        // Show confirmation dialog only if deactivating
        if(noteCategory.isActive) {
            const result = await confirmation.showDeactivate('core:note.category.$long', 1);
            if(result.dialogResponse !== confirmation.options.OK) return;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateNoteCategory(noteCategory.id, !noteCategory.isActive);
            isSuccessful = response.isSuccessful;
            // trigger a reload on the Note Sub Categories tab
            this.events.publish(new NoteCategoryReloadEvent());
            await this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('core:note.category.$long', !noteCategory.isActive, isSuccessful);
            this.state.endSave();
        }
    }
}
